import React, { useCallback, useEffect, useState } from "react";
import { CheckCircle2, XCircle, Info, X, Coins } from "lucide-react";

// 룰렛 결과 스타일의 중앙 축하 토스트. sonner toast.success/error/message/info 를 가로채 표시.
// 계약: window "zm-toast" 이벤트 detail:{type,message} (toastPatch.js). 이 계약은 변경하지 않는다.
const TYPE_META = {
  success: { icon: CheckCircle2, color: "#16A34A" },
  error: { icon: XCircle, color: "#DC2626" },
  info: { icon: Info, color: "#2563EB" },
  message: { icon: Info, color: "#2563EB" },
};
const CONFETTI_COLORS = ["#FACC15", "#F472B6", "#A78BFA", "#34D399", "#60A5FA"];
let _id = 0;

// 메시지에서 포인트 금액 추출 — "+NP" 우선, 없으면 "NP"(천단위 콤마 허용). 없으면 null.
function extractPoints(msg) {
  if (!msg) return null;
  let m = msg.match(/\+\s*(\d[\d,]*)\s*P/);
  if (!m) m = msg.match(/(\d[\d,]*)\s*P/);
  if (!m) return null;
  const n = parseInt(String(m[1]).replace(/,/g, ""), 10);
  if (!Number.isFinite(n) || n <= 0) return null;
  return n;
}

export default function CelebrationToaster() {
  const [toasts, setToasts] = useState([]);
  const remove = useCallback((id) => setToasts((t) => t.filter((x) => x.id !== id)), []);

  useEffect(() => {
    const onToast = (e) => {
      const { type = "message", message = "" } = e.detail || {};
      if (!message) return;
      const id = ++_id;
      setToasts((t) => [...t, { id, type, message }]);
      window.setTimeout(() => remove(id), 3800);
    };
    window.addEventListener("zm-toast", onToast);
    return () => window.removeEventListener("zm-toast", onToast);
  }, [remove]);

  if (toasts.length === 0) return null;

  return (
    <>
      <style>{`
        @keyframes zm-pop {0%{transform:scale(.3) translateY(40px);opacity:0}60%{transform:scale(1.06) translateY(-6px);opacity:1}100%{transform:scale(1) translateY(0);opacity:1}}
        @keyframes zm-confetti {0%{transform:translateY(-30px) rotate(0);opacity:0}10%{opacity:1}100%{transform:translateY(300px) rotate(720deg);opacity:0}}
        @keyframes zm-burst {0%{transform:scale(0);opacity:.9}100%{transform:scale(4);opacity:0}}
        .zm-pop{animation:zm-pop .55s cubic-bezier(.34,1.56,.64,1) forwards}
        .zm-confetti{position:absolute;width:8px;height:12px;border-radius:2px;animation:zm-confetti 2s linear forwards}
        .zm-burst{animation:zm-burst .7s ease-out forwards}
      `}</style>
      {/* 작은(기존) 토스트 스택 — error/message/info + 금액 없는 success */}
      <div
        className="fixed inset-0 z-[70] flex flex-col items-center justify-center gap-3 p-6 pointer-events-none max-w-md mx-auto"
        data-testid="celebration-toaster"
      >
        {toasts
          .filter((t) => !(t.type === "success" && extractPoints(t.message) != null))
          .map((t) => (
            <SmallToast key={t.id} toast={t} onClose={() => remove(t.id)} />
          ))}
      </div>
      {/* 큰 축하 카드 — 포인트 적립 성공(금액 감지) 전용 */}
      {toasts
        .filter((t) => t.type === "success" && extractPoints(t.message) != null)
        .map((t) => (
          <RewardToast key={t.id} toast={t} amount={extractPoints(t.message)} onClose={() => remove(t.id)} />
        ))}
    </>
  );
}

// 기존 소박한 인라인 토스트(변경 금지 대상: error/message/info + 금액 없는 success)
function SmallToast({ toast, onClose }) {
  const meta = TYPE_META[toast.type] || TYPE_META.message;
  const Icon = meta.icon;
  const isSuccess = toast.type === "success";
  const [confetti] = useState(() =>
    isSuccess
      ? Array.from({ length: 18 }, (_, i) => ({
          left: `${i * 5 + Math.random() * 8}%`,
          bg: CONFETTI_COLORS[i % CONFETTI_COLORS.length],
          delay: `${Math.random() * 0.4}s`,
          dur: `${1.6 + Math.random() * 0.7}s`,
        }))
      : []
  );

  return (
    <div className="relative pointer-events-auto zm-pop" data-testid="celebration-toast" data-type={toast.type}>
      {confetti.map((c, i) => (
        <div key={i} className="zm-confetti" style={{ left: c.left, top: "-10px", background: c.bg, animationDelay: c.delay, animationDuration: c.dur }} />
      ))}
      {isSuccess && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className="w-28 h-28 rounded-full zm-burst" style={{ background: "radial-gradient(circle, rgba(250,204,21,.55) 0%, transparent 70%)" }} />
        </div>
      )}
      <div className="relative bg-card text-foreground border border-border rounded-2xl shadow-xl px-5 py-4 min-w-[280px] max-w-[360px] flex items-center gap-3">
        <Icon className="w-7 h-7 shrink-0" style={{ color: meta.color }} />
        <p className="flex-1 text-[15px] font-bold leading-snug whitespace-pre-line">{toast.message}</p>
        <button onClick={onClose} aria-label="닫기" data-testid="celebration-toast-close" className="shrink-0 text-muted-foreground hover:text-foreground tap-scale">
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

// 룰렛 결과 스타일의 큰 축하 카드 — 포인트 적립 성공 전용(큰 +NP 그라데이션 + confetti + 확인 버튼)
function RewardToast({ toast, amount, onClose }) {
  const [confetti] = useState(() =>
    Array.from({ length: 20 }, (_, i) => ({
      left: `${i * 5 + Math.random() * 8}%`,
      bg: CONFETTI_COLORS[i % CONFETTI_COLORS.length],
      delay: `${Math.random() * 0.5}s`,
      dur: `${1.8 + Math.random() * 0.8}s`,
    }))
  );

  return (
    <div
      className="fixed inset-0 z-[71] flex items-center justify-center p-6 pointer-events-auto bg-black/40"
      onClick={onClose}
      data-testid="celebration-toast"
      data-type="success"
    >
      {confetti.map((c, i) => (
        <div key={i} className="zm-confetti" style={{ left: c.left, top: "10%", background: c.bg, animationDelay: c.delay, animationDuration: c.dur }} />
      ))}
      <div
        className="relative bg-card text-foreground rounded-3xl shadow-2xl p-8 text-center max-w-sm w-full zm-pop"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="absolute inset-0 -m-12 flex items-center justify-center pointer-events-none">
          <div className="w-32 h-32 rounded-full zm-burst" style={{ background: "radial-gradient(circle, rgba(250,204,21,0.6) 0%, transparent 70%)" }} />
        </div>
        <div className="relative">
          <p className="text-xs font-bold text-purple-600 uppercase tracking-wider mb-1">🎉 적립 완료!</p>
          <p
            className="text-4xl sm:text-5xl font-black text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-pink-600 my-3 tabular-nums whitespace-nowrap leading-none"
            data-testid="celebration-toast-points"
          >
            +{amount.toLocaleString()}P
          </p>
          <p className="text-xs text-muted-foreground inline-flex items-center gap-1 justify-center whitespace-pre-line">
            <Coins className="w-3 h-3 text-amber-500 shrink-0" />
            {toast.message}
          </p>
          <button
            data-testid="celebration-toast-confirm"
            onClick={onClose}
            className="mt-6 w-full py-3 rounded-2xl font-bold text-white shadow-md tap-scale"
            style={{ background: "#2563EB" }}
          >
            확인
          </button>
        </div>
      </div>
    </div>
  );
}
